import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { id as localeId } from "date-fns/locale";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Timer, Hourglass, Building2, TrendingUp, ArrowRight } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { RSCM } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

// Super Admin's "Waktu Tunggu Mendapat Kamar" on the Dashboard: Pasien Datang -> Dapat Kamar (the Durasi Menunggu
// basis), computed by GET /dashboard/waktu-tunggu under the same filter as the rest of the Dashboard. Every duration
// text comes from the backend, so it matches Jadwal Pasien and the exports.

const Notes = ({ children }) => (
  <div className="mt-3 space-y-0.5 text-[11px] leading-snug text-slate-500">
    <p>Dihitung dari Pasien Datang sampai Dapat Kamar · Mengikuti filter Dashboard</p>
    {children}
  </div>
);

const Tile = ({ label, value, sub, n, testId }) => (
  <div className="min-w-0 rounded-xl border border-slate-200/80 bg-slate-50/60 p-3 sm:p-3.5" data-testid={testId}>
    <p className="text-[11px] font-medium text-slate-500">{label}</p>
    <p className="mt-1 text-base sm:text-lg font-bold text-slate-900 leading-tight break-words">{value}</p>
    {sub && <p className="mt-0.5 text-[11px] text-slate-500 break-words">{sub}</p>}
    <p className="mt-1.5 text-[10px] text-slate-400 tabular-nums">n = {n} pasien</p>
  </div>
);

// Name, then No. RM kept on one line.
const Who = ({ p }) => <>{p.nama_pasien} <span className="whitespace-nowrap">({p.no_rm})</span></>;

const EMPTY = <p className="py-10 text-center text-sm text-slate-400">Belum ada data.</p>;

const fmtPeriode = (iso, mingguan) => {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return mingguan ? `Mg ${format(d, "d MMM", { locale: localeId })}` : format(d, "d MMM", { locale: localeId });
  } catch {
    return iso;
  }
};

export default function WaktuTungguSection({ params }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const key = JSON.stringify(params);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/dashboard/waktu-tunggu", { params: JSON.parse(key) })
      .then((r) => { if (alive) { setData(r.data); setError(""); } })
      .catch((e) => { if (alive) setError(apiError(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [key]);

  const r = data?.ringkasan;
  const n = r?.n ?? 0;
  const sm = data?.sedang_menunggu;
  const mingguan = data?.tren?.satuan === "minggu";
  const tren = (data?.tren?.titik || []).map((t) => ({ ...t, label: fmtPeriode(t.periode, mingguan), jam: Math.round(t.rata_menit / 6) / 10 }));

  return (
    <section className="space-y-4" data-testid="waktu-tunggu-section">
      <div>
        <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Timer size={18} className="text-[#267B84]" /> Waktu Tunggu Mendapat Kamar
        </h2>
        <p className="text-xs text-slate-500 mt-1">Hanya terlihat oleh Super Admin.{error && <span className="text-red-600"> {error}</span>}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-8 border-slate-200/80 shadow-sm" data-testid="waktu-tunggu-statistik">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Pasien yang sudah dapat kamar</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && !data ? <p className="py-10 text-center text-sm text-slate-400">Memuat data...</p> : !r ? (
              <p className="py-10 text-center text-sm text-slate-400" data-testid="waktu-tunggu-kosong">Belum ada data.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3">
                <Tile label="Rata-rata" value={r.rata} n={n} testId="wt-rata" />
                <Tile label="Median" value={r.median} n={n} testId="wt-median" />
                <Tile label="Terlama" value={r.maks} sub={<Who p={r.terlama} />} n={n} testId="wt-maks" />
                <Tile label="Total akumulasi" value={r.total} n={n} testId="wt-total" />
                <Tile label="Pasien dihitung" value={`${n} pasien`} n={n} testId="wt-n" />
                <Tile label={`Melebihi ${data.ambang_hari} hari`} value={`${r.melebihi_persen}%`}
                  sub={`${r.melebihi} dari ${n} pasien`} n={n} testId="wt-melebihi" />
              </div>
            )}
            <Notes>
              <p>Pasien yang pernah dikembalikan ke Waiting List dihitung dari datang pertama sampai kamar terakhir.</p>
              {data?.tanpa_waktu_datang > 0 && (
                <p data-testid="wt-tanpa-datang">{data.tanpa_waktu_datang} pasien tanpa data waktu datang (data sebelum 1 Okt 2026) tidak dihitung.</p>
              )}
              {data?.tanpa_waktu_kamar > 0 && <p>{data.tanpa_waktu_kamar} pasien tanpa waktu dapat kamar tidak dihitung.</p>}
            </Notes>
          </CardContent>
        </Card>

        <Card className="lg:col-span-4 border-slate-200/80 shadow-sm" data-testid="waktu-tunggu-sedang-menunggu">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Hourglass size={16} className="text-amber-600" /> Sedang menunggu
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading && !data ? <p className="py-10 text-center text-sm text-slate-400">Memuat data...</p> : !sm?.jumlah ? (
              <p className="py-6 text-sm text-slate-400" data-testid="wt-menunggu-kosong">Tidak ada pasien yang sudah datang dan masih menunggu kamar.</p>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-2.5 sm:gap-3">
                <Tile label="Sudah datang, belum dapat kamar" value={`${sm.jumlah} pasien`} n={sm.jumlah} testId="wt-menunggu-jumlah" />
                <Tile label="Waktu tunggu berjalan terlama" value={sm.terlama.durasi}
                  sub={<Who p={sm.terlama} />} n={sm.jumlah} testId="wt-menunggu-terlama" />
              </div>
            )}
            <Notes>
              <p>Belum selesai, jadi tidak masuk rata-rata.{" "}
                <Link to="/antrian" className="inline-flex items-center gap-0.5 font-medium text-[#267B84] underline underline-offset-2">
                  Daftar lengkap di Antrian Kamar <ArrowRight size={11} />
                </Link>
              </p>
            </Notes>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-7 border-slate-200/80 shadow-sm" data-testid="waktu-tunggu-per-ksm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Building2 size={16} className="text-[#267B84]" /> Per Departemen
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!data?.per_ksm?.length ? EMPTY : (
              <>
                {/* Phones: one block per Departemen, so the four durations never need a sideways scroll. */}
                <ul className="sm:hidden divide-y divide-slate-100" data-testid="wt-ksm-list">
                  {data.per_ksm.map((k) => (
                    <li key={k.ksm_nama} className="py-2.5">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-sm font-medium text-slate-800 break-words">{k.ksm_nama}</span>
                        <span className="shrink-0 text-[11px] text-slate-500 tabular-nums">n = {k.n}</span>
                      </div>
                      <dl className="mt-1 grid grid-cols-3 gap-2 text-xs">
                        {[["Rata-rata", k.rata], ["Median", k.median], ["Terlama", k.maks]].map(([l, v]) => (
                          <div key={l} className="min-w-0">
                            <dt className="text-[10px] uppercase tracking-wide text-slate-500">{l}</dt>
                            <dd className="font-semibold text-slate-700">{v}</dd>
                          </div>
                        ))}
                      </dl>
                    </li>
                  ))}
                </ul>
                <div className="hidden sm:block overflow-x-auto -mx-6">
                  <table className="w-full text-sm" data-testid="wt-ksm-table">
                    <thead className="bg-[#267B84] text-white">
                      <tr>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold">Departemen</th>
                        <th className="text-right px-3 py-2.5 text-xs font-semibold">n</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold">Rata-rata</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold">Median</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold">Terlama</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.per_ksm.map((k) => (
                        <tr key={k.ksm_nama} className="hover:bg-slate-50">
                          <td className="px-4 py-2.5 font-medium text-slate-800">{k.ksm_nama}</td>
                          <td className="px-3 py-2.5 text-right text-slate-600 tabular-nums">{k.n}</td>
                          <td className="px-3 py-2.5 font-semibold text-slate-800 whitespace-nowrap">{k.rata}</td>
                          <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{k.median}</td>
                          <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{k.maks}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <Notes><p>Urut dari rata-rata terlama.</p></Notes>
          </CardContent>
        </Card>

        <Card className="lg:col-span-5 border-slate-200/80 shadow-sm" data-testid="waktu-tunggu-tren">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <TrendingUp size={16} className="text-[#00B9AD]" /> Tren rata-rata per {mingguan ? "minggu" : "hari"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tren.length < 2 ? (
              <p className="py-10 text-center text-sm text-slate-400" data-testid="wt-tren-kosong">
                {tren.length === 0 ? "Belum ada data." : `Grafik tampil bila ada data di minimal 2 ${mingguan ? "minggu" : "hari"} berbeda.`}
              </p>
            ) : (
              <div data-testid="wt-tren-chart">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={tren} margin={{ top: 12, right: 12, left: -14, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} unit=" j" />
                    <Tooltip formatter={(v, _n, item) => [item.payload.rata, `Rata-rata (n = ${item.payload.n})`]} />
                    <Line type="monotone" dataKey="jam" stroke={RSCM.secondary} strokeWidth={2.5} dot={{ r: 3.5, fill: RSCM.secondary }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            <Notes><p>Dikelompokkan per tanggal Dapat Kamar (WIB); sumbu tegak dalam jam.</p></Notes>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
